import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Flex, message, Modal } from "antd";

import useOptions from "features/masterData/hooks/useOptions";
import {
  dataURLtoFile,
  deleteKeysByList,
  generateRandomString,
} from "utils/helper";
import * as services from "../services/companyApi";
import * as service from "../../employee/services/salaryApi";
import { setCurrentCompanyProfile, setIsLoading } from "../slices/companySlice";
import * as slices from "../../employee/slices/employeeSlice";
import { useTranslation } from "react-i18next";

function toInitialValues(initialValues = {}) {
  const {
    companyType = {},
    province = {},
    district = {},
    subDistrict = {},
  } = initialValues;
  const modifiedValues = {
    ...initialValues,
    companyTypeId: companyType.id,
    companyLogo: initialValues.companyLogo
      ? dataURLtoFile(
        "data:image/png;base64, " + initialValues.companyLogo,
        "example.png"
      )
      : null,
    provinceId: province.provinceId,
    districtId: district.districtId,
    subdistrictId: subDistrict.subdistrictId,
    zipCode: subDistrict.zipCode,
  };
  return modifiedValues;
}

function toParams(params = {}) {
  let modifiedParams = {
    ...params,
    subDistrictId: params.subdistrictId,
    colorCode: ["#1D76DB", "#1D76DB", "#1D76DB", "#1D76DB"],
  };
  modifiedParams = deleteKeysByList(modifiedParams, [
    "companyType",
    "district",
    "province",
    "subDistrict",
  ]);
  return modifiedParams;
}

function useCompanyProfileEdit(props) {
  const { onCancel = () => { }, onClear = () => { } } = props;
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.company.isLoading);
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentCompanyProfile = useSelector(
    (state) => state.company.currentCompanyProfile
  );
  const bankOptions = useSelector((state) => state.employee.bankOptions)
  const {
    companyTypeOptions = [],
    sourceProvinceOptions = [],
    sourceDistrictOptions = [],
    sourceSubDistrictOptions = [],
    getMstCompanyTypeOptions = () => { },
    getProvinceOptions,
    getDistrictOptions,
    getSubDistrictOptions,
    clearDistrictOptions,
    clearSubDistrictOptions,
  } = useOptions();

  const initialValues = useMemo(() => {
    return toInitialValues(currentCompanyProfile);
  }, [currentCompanyProfile]);

  const provinceOptionsTh = useMemo(() => {
    const modifiedOptions = sourceProvinceOptions.map((opt) => ({
      ...opt,
      label: opt.provinceName,
      value: opt.provinceId,
    })).sort((a, b) => a.label.localeCompare(b.label));
    return modifiedOptions;
  }, [sourceProvinceOptions]);

  const provinceOptionsEn = useMemo(() => {
    const modifiedOptions = sourceProvinceOptions.map((opt) => ({
      ...opt,
      label: opt.provinceNameEn,
      value: opt.provinceId,
    })).sort((a, b) => a.label.localeCompare(b.label));
    return modifiedOptions;
  }, [sourceProvinceOptions]);

  const districtOptionsTh = useMemo(() => {
    const modifiedOptions = sourceDistrictOptions.map((opt) => ({
      ...opt,
      label: opt.districtName,
      value: opt.districtId,
    })).sort((a, b) => a.label.localeCompare(b.label));
    return modifiedOptions;
  }, [sourceDistrictOptions]);

  const districtOptionsEn = useMemo(() => {
    const modifiedOptions = sourceDistrictOptions.map((opt) => ({
      ...opt,
      label: opt.districtNameEn,
      value: opt.districtId,
    })).sort((a, b) => a.label.localeCompare(b.label));
    return modifiedOptions;
  }, [sourceDistrictOptions]);

  const subDistrictOptionsTh = useMemo(() => {
    const modifiedOptions = sourceSubDistrictOptions.map((opt) => ({
      ...opt,
      label: opt.subDistrictName,
      value: opt.subDistrictId,
    })).sort((a, b) => a.label.localeCompare(b.label));
    return modifiedOptions;
  }, [sourceSubDistrictOptions]);

  const subDistrictOptionsEn = useMemo(() => {
    const modifiedOptions = sourceSubDistrictOptions.map((opt) => ({
      ...opt,
      label: opt.subDistrictNameEn,
      value: opt.subDistrictId,
    })).sort((a, b) => a.label.localeCompare(b.label));
    return modifiedOptions;
  }, [sourceSubDistrictOptions]);

  const useFetchOptions = (serviceFunction, companyId, groupType, dispatchFunction) => {
    const dispatch = useDispatch();
    useEffect(() => {
      const fetchData = async () => {
        dispatch(slices.setIsLoading(true));
        try {
          const response = await serviceFunction({
            requestId: generateRandomString(),
            companyId,
            groupType,
          });
          dispatch(dispatchFunction(response?.data));
        } catch (error) {
          console.error('Failed to fetch data:', error);
        } finally {
          dispatch(slices.setIsLoading(false));
        }
      };

      if (companyId) {
        fetchData();
      }
    }, [companyId, serviceFunction, groupType, dispatchFunction, dispatch]);
  };
  useFetchOptions(service.getBankOption, currentCompany, "", slices.setBankOptions);

  const modifiedOptions = (options) =>
    options?.map((opt) => ({
      ...opt,
      label: <Flex>
        {opt?.icon && (
          <img
            src={opt?.icon}
            alt="Bank icon"
            style={{ width: "2.3vw", height: "3.8vh", marginRight: "5px" }} />
        )}
        {i18n.language === "en" ? (opt?.nameEn ? opt?.nameEn : opt.name) : opt.name}</Flex>,
      value: opt.id,
    }));

  const modifiedBankOptions = useMemo(() => modifiedOptions(bankOptions), [bankOptions, i18n.language]);

  const getCompany = useCallback(async () => {
    try {
      dispatch(setIsLoading(true));
      const response = await services.getCompany({
        requestId: generateRandomString(),
        companyId: currentCompany,
      });
      dispatch(setCurrentCompanyProfile(response.data));
    } catch (error) {
      message.error(t("common.error"))
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [currentCompany, dispatch]);

  const editCompany = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        let mod = {
          ...toParams(values),
          bankAccount: null,
          ['bankAccount.id']: values.bankAccount
        }

        await services.editCompany(mod);
        message.success(t("common.success"))

        onClear()
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [dispatch, onCancel, onClear]
  );

  const handleProviceChange = useCallback(
    (val) => {
      clearDistrictOptions();
      clearSubDistrictOptions();
      if (val) {
        getDistrictOptions({ provinceId: val });
      }
    },
    [clearDistrictOptions, clearSubDistrictOptions, getDistrictOptions]
  );

  const handleDistrictChange = useCallback(
    (val) => {
      clearSubDistrictOptions();
      if (val) {
        getSubDistrictOptions({ districtId: val });
      }
    },
    [clearSubDistrictOptions, getSubDistrictOptions]
  );

  const onSubmit = useCallback(
    async (values) => {
      const modifiedValues = {
        ...values,
        requestId: generateRandomString(),
        companyId: currentCompany,
      };
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (
          i18n.language === "en"
            ? "Are you sure confirm to edit company profile ?"
            : "คุณยืนยันการแก้ไขข้อมูลบริษัทใช่หรือไม่ ?"
        ),
        onOk() {
          editCompany(modifiedValues);
        },
        okText: t("common.confirm").toUpperCase(),
        cancelText: t("common.cancel").toUpperCase(),
      });
    },
    [currentCompany, editCompany]
  );

  useEffect(() => {
    if (currentCompany) {
      getCompany();
    }
  }, [currentCompany, getCompany]);

  useEffect(() => {
    getMstCompanyTypeOptions();
  }, [getMstCompanyTypeOptions]);

  useEffect(() => {
    getProvinceOptions();
  }, [getProvinceOptions]);

  useEffect(() => {
    if (currentCompanyProfile) {
      const { province = {}, district = {} } = currentCompanyProfile;

      if (province.provinceId)
        getDistrictOptions({ provinceId: province.provinceCode });
      if (district.districtId)
        getSubDistrictOptions({ districtId: district.districtCodeId });
    }
  }, [currentCompanyProfile]);

  return {
    isLoading,
    initialValues,
    companyTypeOptions,
    provinceOptionsTh,
    provinceOptionsEn,
    districtOptionsTh,
    districtOptionsEn,
    subDistrictOptionsTh,
    subDistrictOptionsEn,
    bankOptions: modifiedBankOptions,
    onCancel,
    onSubmit,
    handleProviceChange,
    handleDistrictChange,
  };
}

export default useCompanyProfileEdit;
